<template>
  <div
    class="badge px-3 py-3 gap-2"
    :class="[
      variantClass,
      sizeClass,
      outlined && 'badge-outline',
      customClass,
    ]"
  >
    <slot></slot>
    <button
      v-if="closable"
      @click="onClose"
      class="btn btn-xs btn-circle btn-ghost"
      tabindex="0"
      role="button"
      aria-label="Close"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        class="w-4 h-4"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue'

/**
 * DChip component properties
 */
const props = defineProps({
  /**
   * Determines the color variant of the chip
   * @values primary, secondary, accent, info, success, warning, error, neutral
   */
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => {
      return [
        'primary',
        'secondary',
        'accent',
        'info',
        'success',
        'warning',
        'error',
        'neutral',
        null,
      ].includes(value)
    },
  },
  
  /**
   * Makes the chip outlined rather than filled
   */
  outlined: {
    type: Boolean,
    default: false,
  },
  
  /**
   * Determines the size of the chip
   * @values xs, sm, md, lg, xl
   */
  size: {
    type: String,
    default: null,
    validator: (value) => {
      return ['xs', 'sm', 'md', 'lg', 'xl', null].includes(value)
    },
  },
  
  /**
   * Whether the chip can be closed
   */
  closable: {
    type: Boolean,
    default: false,
  },
  
  /**
   * Additional CSS classes to apply to the chip
   */
  customClass: {
    type: String,
    default: '',
  },
})

/**
 * Computed property for variant class to ensure Tailwind sees the full class name
 */
const variantClass = computed(() => {
  if (!props.variant) return null
  
  // List all possible variant classes explicitly so Tailwind can detect them
  switch(props.variant) {
    case 'primary': return 'badge-primary'
    case 'secondary': return 'badge-secondary'
    case 'accent': return 'badge-accent'
    case 'info': return 'badge-info'
    case 'success': return 'badge-success'
    case 'warning': return 'badge-warning'
    case 'error': return 'badge-error'
    case 'neutral': return 'badge-neutral'
    default: return null
  }
});

/**
 * Computed property for size class to ensure Tailwind sees the full class name
 */
const sizeClass = computed(() => {
  if (!props.size) return null
  
  // List all possible size classes explicitly so Tailwind can detect them
  switch(props.size) {
    case 'xs': return 'badge-xs'
    case 'sm': return 'badge-sm'
    case 'md': return 'badge-md'
    case 'lg': return 'badge-lg'
    case 'xl': return 'badge-xl'
    default: return null
  }
})

/**
 * Events emitted by the chip component
 */
const emit = defineEmits(['close'])

/**
 * Handles the close button click event
 */
const onClose = () => {
  emit('close')
}
</script>